/**
 * The glyph picker's searchable set, transcribed from the design prototype
 * (`studio/glyph-picker.jsx`). Deliberately a CURATED list rather than the whole
 * Lucide collection (which is what `IconPickerAtom` offers): a type's mark is
 * chosen by what the thing IS, so the keywords — not the icon's own name — are
 * what a person types. Searching 1,400 icons by filename is a different tool.
 *
 * The values are real Iconify `set:name` strings, so what is picked here is what
 * is stored on the schema and what `GlyphAtom` renders back.
 */
export interface GlyphPickerIcon {
  /** Iconify `set:name`, e.g. `lucide:building-2`. */
  name: string;
  /** Space-separated search keywords — what someone would actually type. */
  keywords: string;
}

export const GLYPH_PICKER_ICONS: GlyphPickerIcon[] = [
  { name: 'lucide:circle', keywords: 'default circle round' },
  { name: 'lucide:building-2', keywords: 'org company vendor building office' },
  { name: 'lucide:handshake', keywords: 'deal partner vendor supplier agreement' },
  { name: 'lucide:layers', keywords: 'product stack platform layers' },
  { name: 'lucide:bug', keywords: 'defect bug issue quality' },
  { name: 'lucide:shield-check', keywords: 'quality assurance verified compliance' },
  { name: 'lucide:users', keywords: 'team people group members' },
  { name: 'lucide:user', keywords: 'person identity user contact' },
  { name: 'lucide:box', keywords: 'product item package sku' },
  { name: 'lucide:package', keywords: 'shipment package release' },
  { name: 'lucide:file-text', keywords: 'document contract record file' },
  { name: 'lucide:target', keywords: 'goal objective target measure' },
  { name: 'lucide:calendar', keywords: 'date period calendar schedule' },
  { name: 'lucide:calendar-clock', keywords: 'deadline schedule renewal timing' },
  { name: 'lucide:coins', keywords: 'money budget cost finance value' },
  { name: 'lucide:credit-card', keywords: 'payment billing spend' },
  { name: 'lucide:gauge', keywords: 'measure metric health score' },
  { name: 'lucide:trending-up', keywords: 'growth pipeline velocity performance' },
  { name: 'lucide:percent', keywords: 'rate ratio percentage escape' },
  { name: 'lucide:flag', keywords: 'milestone flag status' },
  { name: 'lucide:map-pin', keywords: 'location site address place' },
  { name: 'lucide:git-branch', keywords: 'arc branch stage workflow' },
  { name: 'lucide:link', keywords: 'relationship link reference' },
  { name: 'lucide:database', keywords: 'type schema record data' },
  { name: 'lucide:store', keywords: 'marketplace vendor shop channel' },
  { name: 'lucide:wrench', keywords: 'maintenance service repair' },
  { name: 'lucide:beaker', keywords: 'test experiment trial lab' },
  { name: 'lucide:clipboard-check', keywords: 'review approval checklist audit' },
  { name: 'lucide:megaphone', keywords: 'campaign marketing announcement' },
  { name: 'lucide:life-buoy', keywords: 'support ticket help incident' },
  { name: 'lucide:zap', keywords: 'incident event trigger alert' },
  { name: 'lucide:lock', keywords: 'security private restricted' },
];

/**
 * Theme tokens are offered FIRST and are the only coloured option with a plain
 * name, because they follow light/dark mode and survive a rebrand — the hue and
 * hex paths exist for marks that must match something outside the theme.
 *
 * `token` is the design-system variable base name: `neutral` → `var(--neutral)`,
 * which is exactly what `GlyphAtom` resolves.
 */
export interface GlyphPickerToken {
  token: string;
  labelKey: string;
}

export const GLYPH_PICKER_TOKENS: GlyphPickerToken[] = [
  { token: 'neutral', labelKey: 'studio.typeEditor.glyphPicker.token.neutral' },
  { token: 'primary', labelKey: 'studio.typeEditor.glyphPicker.token.primary' },
  { token: 'success', labelKey: 'studio.typeEditor.glyphPicker.token.success' },
  { token: 'warning', labelKey: 'studio.typeEditor.glyphPicker.token.warning' },
  { token: 'danger', labelKey: 'studio.typeEditor.glyphPicker.token.danger' },
  { token: 'info', labelKey: 'studio.typeEditor.glyphPicker.token.info' },
  { token: 'accent', labelKey: 'studio.typeEditor.glyphPicker.token.accent' },
];

/** Matches every whitespace-separated term against name + keywords, as the prototype does. */
export function searchGlyphIcons(query: string): GlyphPickerIcon[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return GLYPH_PICKER_ICONS;
  return GLYPH_PICKER_ICONS.filter((icon) => {
    const haystack = `${icon.name} ${icon.keywords}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

/** A glyph string is a LETTER when it is not an Iconify `set:name`. */
export const isLetterGlyph = (glyph: string | null | undefined): boolean =>
  !!glyph && !glyph.includes(':');
